import { useContext, useEffect, useState, useRef } from 'react';
import { Context } from '../Context';
import { useTranslation } from 'react-i18next';

import ToolTip from './ToolTip';

import { countUnitsOnRefTier } from '../utils/utilityFunctions';

import styles from '../styles/bim.module.scss';

import { AVAILABILITY_STATUS } from '../constants/availabilityStatus';
import { MOBILE_VIEW_TYPES } from '../constants/mobileViewTypes';
import { MODEL_SCOPE } from '../constants/modelScope';
const { SOLD, AVAILABLE } = AVAILABILITY_STATUS;
const { TIERS_VIEW } = MOBILE_VIEW_TYPES;
const { SITE, EDIFICE } = MODEL_SCOPE;

function ProjectVisualization({ edifice }) {
	const {
		projectSetup,
		dataUrl,
		tiersViewRef,
		isDesktop,
		isMobile,
		loading,
		activeApartment,
		activeApartmentData,
		setActiveApartmentHandle,
		activeBuilding,
		setActiveBuilding,
		buildingsData,
		tiersData,
		displayableApartmentsData: apartmentsData,
		allProcessedApartmentsData,
		filteredApartmentData,
		isFilterActive,
		activeTier,
		setActiveTier,
		setMobileActiveView,
		setShowView,
		updateUrlHash,
		setCurrentModelScope,
	} = useContext(Context);

	const [visImgName, setVisImgName] = useState(null);
	const [visLayer, setVislayer] = useState(null);
	const [buildingData, setBuildingData] = useState(null);
	const [activeFloor, setActiveFloor] = useState(null);
	const [tooltipVisible, setTooltipVisible] = useState(false);
	const [tooltipContent, setTooltipContent] = useState({
		tier: '',
		apartments: 0,
	});

	const visLayerRef = useRef(null);
	const listenersRef = useRef([]);
	const lastClickedMobileElementRef = useRef(null);

	const { t } = useTranslation();

	const isSiteModel = projectSetup?.site?.render_360 ?? false;
	const isBuildingModel = projectSetup?.buildings?.render_360 ?? false;
	const isModelView = isSiteModel || isBuildingModel;
	const areSoldAccessible =
		projectSetup?.website?.are_sold_apartments_accessible ?? false;

	// const visualizationsData = projectSetup?.site?.render ?? [];
	const visualizationsData = [
		//empty array means no render
		{
			file_name: 'main',
			breakpoint: 1440,
			outlines: true,
		},
	];

	function getApartmentTypeLabel(apartment) {
		if (!apartment?.type) return '';
		const firstLetter = apartment.type[0].toUpperCase();
		if (firstLetter === 'M') return t('apartment');
		if (firstLetter === 'U') return t('unit');
		if (firstLetter === 'B') return t('premises');
		return '';
	}

	// const isSingleEdifice = buildingsData && buildingsData.length === 1;

	// useEffect(() => {

	//     if (edifice && buildingsData) {
	//         const edificeData = buildingsData.find(edificeItem => edificeItem.name_original === edifice.name);
	//         setEdificeData(edificeData);
	//     }

	// }, [loading]);

	// get imgs and layers
	useEffect(() => {
		async function getVisualizationImage(deviceScreenSize = 1440) {
			try {
				if (visualizationsData && visualizationsData.length > 0) {
					//TODO select appropriate visualization based on screen size from argument
					const visualization =
						visualizationsData.find(
							(vis) => vis.breakpoint === deviceScreenSize
						) || visualizationsData[0];
					const visSvgData = await fetch(
						`${dataUrl}vis/${visualization.file_name}.svg`
					).then((res) => res.text());
					setVisImgName(visualization.file_name);
					setVislayer(visSvgData);
				}
			} catch (error) {
				console.error('Visualization Img data fetch Failed', error);
			}
		}
		getVisualizationImage(1440); //TODO: pass device screen size to fetch appropriate image
	}, [loading]);

	useEffect(() => {
		if (visLayer && visLayerRef && buildingsData) {
			// Clear previous listeners and reset listenersRef
			listenersRef.current.forEach(({ element, type, handler }) => {
				element.removeEventListener(type, handler);
			});
			listenersRef.current = [];

			let currentTiers = tiersData;
			let allApartmentsFromData = apartmentsData; //rather all displayable apartments ?
			let visibleFilteredApartments = filteredApartmentData;

			const highestSelectableRefTier = //TODO: refactor to check if buildingTiers have compartments, only those should be counted
				buildingsData?.reduce((max, building) => {
					return Math.max(max, building.levels) - 1; // -1 because ref_tier starts from 0 and levels is tiers.length (1-based)
				}, 0) || 0;

			const svgElement = visLayerRef.current.querySelector('svg');

			// const overviewPaths = svgElement.querySelectorAll('path');
			const overviewPaths = svgElement.querySelectorAll('path[id]'); //TEMP - remove
			// const buildingPaths = svgElement.querySelectorAll('path[data-building]');
			// const tierPaths = svgElement.querySelectorAll('path[data-tier]');
			// const unitPaths = svgElement.querySelectorAll('path[data-uuid]');
			
			//new approach. there will always be only paths with [id] attribute. BUT depending on its value it can be building, tier or unit
			// we take id and split it by '-' to determine type; one part will be 'building', 2 or 3 parts will be 'tier' and 5 parts will mean 'unit'

			const buildingPaths = [];
			const tierPaths = [];
			const unitPaths = [];
			
			for (const path of overviewPaths) {
				const id = path.getAttribute('id');
				const parts = id.split('-');
				if (parts.length === 1 && parts[0].length === 2) { //to prevent old integer ids from interfering as building id is two char.
					buildingPaths.push(path);
				} else if (parts.length === 2 || parts.length === 3) {
					tierPaths.push(path);
				} else if (parts.length === 5) {
					unitPaths.push(path);
				}
			}

			console.log('overviewPaths', overviewPaths);
			console.log('buildingPaths', buildingPaths);

			// BUILDING OUTLINES LOGIC --------------------------------------------------------------------------------------------------------------------
			for (const buildingPath of buildingPaths) {
				if (!buildingPath) continue;

				// const buildingId = buildingPath.getAttribute('data-building');
				const buildingId = buildingPath.getAttribute('id'); //id_cad
				const buildingName = buildingsData.find(
					(building) => building.id_cad === buildingId
				)?.name_original;

				if (activeBuilding === buildingName) {
					buildingPath.classList.add('active_path');
				} else {
					buildingPath.classList.remove('active_path');
				}

				const tiersInBuilding = tiersData
					.filter((tier) => tier.building_id === buildingId)
					.map((tier) => tier.ref_tier);
				const allApartmentsInBuilding = allApartmentsFromData.filter(
					(apartment) => apartment.building_id === buildingId
				);
				const filteredApartmentsInBuilding = visibleFilteredApartments.filter(
					(apartment) => apartment.building_id === buildingId
				);
				const apartmentsInBuildingCount = tiersInBuilding?.reduce(
					(count, tier) => {
						return (
							count + countUnitsOnRefTier(allApartmentsInBuilding, tier, true)
						);
					},
					0
				);
				const filteredApartmentsInBuildingCount = tiersInBuilding?.reduce(
					(count, tier) => {
						return (
							count +
							countUnitsOnRefTier(filteredApartmentsInBuilding, tier, true)
						);
					},
					0
				);

				const countValueText =
					filteredApartmentsInBuildingCount !== apartmentsInBuildingCount
						? `${filteredApartmentsInBuildingCount}/${apartmentsInBuildingCount}`
						: `${apartmentsInBuildingCount}`;

				const activeBuildingData = buildingsData.find(
					(e) => e.name_original === buildingName
				);
				const buildingDisplayName =
					activeBuildingData?.name_original ||
					activeBuildingData?.name ||
					buildingName;
				const buildingTooltipContent = {
					title: `${t('building')} - ${buildingDisplayName}`,
					apartments: allApartmentsInBuilding.length
						? `${
								isFilterActive ? `${t('filtered_out')}: ` : ''
						  }${countValueText}`
						: `${t('lack_of')}`,
					btntext: `${t('see_building_details')}`,
					setScope: setCurrentModelScope,
				};

				const handleBuildingMouseOver = () => {
					if (!isMobile) {
						setTooltipContent(buildingTooltipContent);
						setTooltipVisible(true);
					}
				};

				const handleBuildingMouseLeave = () => {
					if (!isMobile) {
						setTooltipVisible(false);
						setTooltipContent(null);
					}
				};

				const handleBuildingClick = () => {
					updateUrlHash(`${t('building')}${buildingDisplayName}`);
					setActiveBuilding(buildingName);
					if (isDesktop) {
						setCurrentModelScope(EDIFICE);
						// setActiveLayer(APARTMENTS_LAYER);
						setTooltipVisible(false);
						setTooltipContent(null);
					} else if (isMobile) {
						if (lastClickedMobileElementRef.current === buildingPath) {
							// setMobileActiveView(TIERS_VIEW);
							// setActiveLayer(APARTMENTS_LAYER);
							setCurrentModelScope(EDIFICE);
							setTooltipVisible(false);
							setTooltipContent(null);
							lastClickedMobileElementRef.current = null;
						} else {
							// setActiveLayer(BUILDINGS_LAYER);
							setTooltipContent(buildingTooltipContent);
							setTooltipVisible(true);
							lastClickedMobileElementRef.current = buildingPath;
						}
					}
				};

				buildingPath.addEventListener('mouseover', handleBuildingMouseOver);
				listenersRef.current.push({
					element: buildingPath,
					type: 'mouseover',
					handler: handleBuildingMouseOver,
				});

				buildingPath.addEventListener('mouseleave', handleBuildingMouseLeave);
				listenersRef.current.push({
					element: buildingPath,
					type: 'mouseleave',
					handler: handleBuildingMouseLeave,
				});

				buildingPath.addEventListener('click', handleBuildingClick);
				listenersRef.current.push({
					element: buildingPath,
					type: 'click',
					handler: handleBuildingClick,
				});
			}

			// TIER LAYER LOGIC ---------------------------------------------------------------------------------------------------------------------------
			for (const tierPath of overviewPaths) {
				const floorIdString = tierPath.getAttribute('id'); //id_cad ?
				const floorIdInt = parseInt(floorIdString, 10);

				console.log(
					'floorIdInt',
					floorIdInt,
					'highestSelectableRefTier',
					highestSelectableRefTier
				);

				if (buildingsData && floorIdInt > highestSelectableRefTier) {
					tierPath.classList.add(styles.not_in_scope);
					return;
				}

				let apartmentsOnTierCount = 0;
				let filteredApartmentsOnTierCount = 0;

				apartmentsOnTierCount = countUnitsOnRefTier(
					allApartmentsFromData,
					floorIdInt
				);
				filteredApartmentsOnTierCount = countUnitsOnRefTier(
					visibleFilteredApartments,
					floorIdInt
				);

				const countValueText =
					filteredApartmentsOnTierCount !== apartmentsOnTierCount
						? `${filteredApartmentsOnTierCount}/${apartmentsOnTierCount}`
						: `${apartmentsOnTierCount}`;

				const tierData = currentTiers.find((t) => t.ref_tier === floorIdInt);
				const tierName = tierData?.name ?? floorIdString;

				if (activeTier == floorIdString) {
					tierPath.classList.add('active_path');
				} else {
					tierPath.classList.remove('active_path');
				}

				const tierTooltipContent = {
					title: `${t('tier')} - ${tierName}`,
					apartments:
						apartmentsOnTierCount != 0
							? `${
									isFilterActive ? `${t('filtered_out')}: ` : ''
							  }${countValueText}`
							: `${t('lack_of')}`,
					btntext: `${t('see_floor')}`,
				};

				const handleTierMouseOver = () => {
					if (!isMobile) {
						setTooltipContent(tierTooltipContent);
						setTooltipVisible(true);
						setActiveFloor(floorIdInt);
					}
				};

				const handleTierMouseLeave = () => {
					if (!isMobile) {
						setTooltipVisible(false);
						setActiveFloor(null);
						setTooltipContent(null);
					}
				};

				const handleTierClick = () => {
					setActiveTier(floorIdInt);
					updateUrlHash('tiers');
					if (!activeBuilding)
						setActiveBuilding(buildingsData[0].name_original);

					if (isDesktop) {
						tiersViewRef?.current?.scrollIntoView({
							behavior: 'smooth',
							block: 'start',
						});
						setTooltipVisible(false);
						setTooltipContent(null);
					} else if (isMobile) {
						if (lastClickedMobileElementRef.current === tierPath) {
							if (!activeBuilding)
								setActiveBuilding(buildingsData[0].name_original);
							setActiveTier(floorIdInt);
							setMobileActiveView(TIERS_VIEW);
							setTooltipVisible(false);
							setTooltipContent(null);
							setActiveFloor(null);
							lastClickedMobileElementRef.current = null;
						} else {
							setTooltipContent(tierTooltipContent);
							setTooltipVisible(true);
							setActiveFloor(floorIdInt);
							lastClickedMobileElementRef.current = tierPath;
						}
					}
				};

				tierPath.addEventListener('mouseover', handleTierMouseOver);
				listenersRef.current.push({
					element: tierPath,
					type: 'mouseover',
					handler: handleTierMouseOver,
				});

				tierPath.addEventListener('mouseleave', handleTierMouseLeave);
				listenersRef.current.push({
					element: tierPath,
					type: 'mouseleave',
					handler: handleTierMouseLeave,
				});

				tierPath.addEventListener('click', handleTierClick);
				listenersRef.current.push({
					element: tierPath,
					type: 'click',
					handler: handleTierClick,
				});
			}

			// APARTMENT LAYER LOGIC ----------------------------------------------------------------------------------------------------------------------
			for (const unitPath of unitPaths) {
				if (!unitPath) continue;
				// const apartmentUuidFromPath = unitPath.getAttribute('data-uuid');
				const apartmentUuidFromPath = unitPath.getAttribute('id'); //uuid or id_cad - to be confirmed
				const apartmentIdFromPath = allApartmentsFromData.find(
					(apt) => apt.uuid === apartmentUuidFromPath
				)?.id_original;

				const apartmentData = allApartmentsFromData.find(
					(apt) => apt.uuid === apartmentUuidFromPath
				);
				const isVisibleAfterFilter = visibleFilteredApartments.some(
					(apt) => apt.uuid === apartmentUuidFromPath
				);

				if (apartmentData) {
					unitPath.setAttribute('data-rooms', parseInt(apartmentData.rooms));
					// unitPath.setAttribute('data-uuid', apartmentData.uuid);
				}

				if (activeApartment === apartmentIdFromPath) {
					unitPath.classList.add('active_path');
				} else {
					unitPath.classList.remove('active_path');
				}

				if (isVisibleAfterFilter) {
					if (!apartmentData?.availability) {
						unitPath.classList.add('rooms-color');
					} else if (apartmentData?.availability === SOLD) {
						unitPath.classList.add('state-sell');
					} else {
						unitPath.classList.add('rooms-color');
					}
				} else {
					unitPath.classList.remove('rooms-color');
					unitPath.classList.remove('state-sell');
				}

				let apartmentTooltipContent = {};
				let filteredApartmentTooltipContent = {};
				let isInaccessibleTooltipContent = {
					title: t('inaccessible'), //TODO: when SimilarApartments feature is implemented, inform user about found similar apartments
				};

				if (apartmentData) {
					const apartmentLabel = getApartmentTypeLabel(apartmentData);
					const title = `${apartmentLabel} - ${
						apartmentData.id_original ?? apartmentData.id_original
					}`;
					const surface = `${t('surface')}: ${apartmentData.area_original}`;
					const rooms = `${t('rooms')}: ${apartmentData.rooms}`;
					const availability = apartmentData.availability ?? AVAILABLE;
					const tierForApartmentData = currentTiers.find(
						(t) => t.ref_tier === apartmentData.ref_tier
					);
					const tierNameForApartment =
						tierForApartmentData?.name_original ??
						(apartmentData.ref_tier || '');

					const buildingTiersForApartmentData = currentTiers.filter(
						(tier) => tier.edifice_id === apartmentData.building_id
					);
					// const tiersForApartmentData = currentTiers.filter(tier => apartmentData.tiers.map(t => t.ref_tier).includes(tier.ref_tier)) || [];
					const tiersForApartmentData =
						buildingTiersForApartmentData.filter((tier) =>
							apartmentData.tiers.map((t) => t.ref_tier).includes(tier.ref_tier)
						) || [];
					const tierNamesForApartment = tiersForApartmentData.map(
						(tier) => tier.name_original || tier.ref_tier
					);

					const tierText =
						tierNamesForApartment.length === 1
							? `${t('tier')}: ${tierNameForApartment}`
							: `${t('tiers')}: ${tierNamesForApartment.join(', ')}`;
					const btnText = projectSetup.website.are_sold_apartments_accessible
						? t('see_apartment')
						: availability === SOLD
						? null
						: t('see_apartment');

					apartmentTooltipContent = {
						title: title,
						surface,
						rooms,
						tier: tierText,
						availability,
						btntext: btnText,
						id: apartmentIdFromPath,
						behavior: 1,
					};

					filteredApartmentTooltipContent = {
						// TODO: if scrollTo filter settings is implemented, inform user that scroll/open will happen
						title: title,
						comment: `${t('unit')} ${t('hidden_by_filter')}`,
					};
				}

				const handleApartmentMouseOver = () => {
					if (isMobile) return;

					if (apartmentData) {
						if (!isVisibleAfterFilter) {
							setTooltipContent(filteredApartmentTooltipContent);
						} else {
							setTooltipContent(apartmentTooltipContent);
						}
					} else {
						setTooltipContent(isInaccessibleTooltipContent);
					}
					setTooltipVisible(true);
				};

				const handleApartmentMouseLeave = () => {
					if (!isMobile) {
						setTooltipVisible(false);
						setTooltipContent(null);
					}
				};

				const handleApartmentClick = () => {
					if (!apartmentData) {
						if (isMobile) {
							setTooltipContent(isInaccessibleTooltipContent);
							setTooltipVisible(true);
							lastClickedMobileElementRef.current = null;
						} else return; // TODO: in future we will handle this case differently, by showing similar apartments;
					}
					if (!isVisibleAfterFilter) {
						if (isMobile) {
							setTooltipContent(filteredApartmentTooltipContent);
							setTooltipVisible(true);
							lastClickedMobileElementRef.current = null;
						} else return; // TODO: possibly scroll to filter settings
					}
					if (apartmentData.availability === SOLD && !areSoldAccessible) {
						return;
					}

					updateUrlHash('apartment');

					if (isDesktop) {
						setTooltipContent(null);
						setTooltipVisible(false);
						setActiveApartmentHandle(apartmentIdFromPath);
					} else if (isMobile) {
						if (lastClickedMobileElementRef.current === unitPath) {
							setActiveApartmentHandle(apartmentIdFromPath);
							lastClickedMobileElementRef.current = null;
						} else {
							setTooltipContent(apartmentTooltipContent);
							setTooltipVisible(true);
							lastClickedMobileElementRef.current = unitPath;
						}
					}
				};

				unitPath.addEventListener('mouseover', handleApartmentMouseOver);
				listenersRef.current.push({
					element: unitPath,
					type: 'mouseover',
					handler: handleApartmentMouseOver,
				});

				unitPath.addEventListener('mouseleave', handleApartmentMouseLeave);
				listenersRef.current.push({
					element: unitPath,
					type: 'mouseleave',
					handler: handleApartmentMouseLeave,
				});

				unitPath.addEventListener('click', handleApartmentClick);
				listenersRef.current.push({
					element: unitPath,
					type: 'click',
					handler: handleApartmentClick,
				});
			}
		

		const handleOutsideMobileClick = (event) => {
			if (!isMobile || !tooltipVisible) {
				return;
			}

			const clickedElement = event.target;
			const clickedPath = clickedElement.closest('path');

			let shouldCloseTooltip = true;

			if (clickedPath) {
				const isHandledInteractivePath = unitPaths.some(
					(p) => p === clickedPath
				);

				if (isHandledInteractivePath) {
					shouldCloseTooltip = false;
				}
			}

			if (shouldCloseTooltip) {
				setTooltipVisible(false);
				setTooltipContent(null);
				if (lastClickedMobileElementRef) {
					lastClickedMobileElementRef.current = null;
				}
			}
		};

		const container = visLayerRef.current;
		if (container) {
			container.addEventListener('click', handleOutsideMobileClick);
		}

		return () => {
			listenersRef.current.forEach(({ element, type, handler }) => {
				element.removeEventListener(type, handler);
			});
			listenersRef.current = [];
			if (container) {
				container.removeEventListener('click', handleOutsideMobileClick);
			}
		};
	}
	}, [
		visLayer,
		buildingsData,
		activeTier,
		activeBuilding,
		filteredApartmentData,
		isFilterActive,
	]);

	useEffect(() => {
		if (visLayerRef.current) {
			const svgElement = visLayerRef.current.querySelector('svg');

			if (svgElement) {
				svgElement.setAttribute('preserveAspectRatio', 'xMidYMid slice');

				const imageElement = svgElement.querySelector('image');
				const imageOriginalHref = imageElement?.getAttribute('href');
				imageElement?.setAttribute(
					'href',
					`${dataUrl}vis/${imageOriginalHref}`
				);
			}
		}
	}, [visLayer]);

	return (
		<div className={styles.visualization}>
			{visLayer && (
				<div
					className={styles.vis_layer}
					dangerouslySetInnerHTML={{ __html: visLayer }}
					ref={visLayerRef}
				/>
			)}

			{tooltipVisible && (
				<ToolTip
					content={tooltipContent}
					setTooltipVisible={setTooltipVisible}
				/>
			)}
			{isDesktop && (
				<div className={styles.floor_counter}>
					{buildingData && (activeFloor || activeFloor === 0) ? (
						<>
							{t('see_floor')}:{' '}
							{buildingData.tiers[activeFloor]?.name_original ?? ''}
						</>
					) : (
						<>{t('select_floor')}</>
					)}
				</div>
			)}
			{isDesktop && isModelView && (
				<button
					className={`${styles.btn} ${styles.show_model_btn}`}
					onClick={() => {
						setShowView(1);
						updateUrlHash('model');
						setCurrentModelScope(isSiteModel ? SITE : EDIFICE);
					}}
				>
					{t('see_model')}
				</button>
			)}
		</div>
	);
}

export default ProjectVisualization;
